import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Save, Download, FileText, ArrowLeft, Loader2, Globe, MessageSquare, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function SmartEditor({ user, logout }) {
  const { contentId } = useParams();
  const navigate = useNavigate();
  const [content, setContent] = useState(null);
  const [editedText, setEditedText] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [exportFormat, setExportFormat] = useState("dita");
  const [outputFormat, setOutputFormat] = useState("pdf");
  const [exportData, setExportData] = useState(null);
  const [showValidationNotice, setShowValidationNotice] = useState(true);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [translating, setTranslating] = useState(false);
  const [targetLang, setTargetLang] = useState("es");

  useEffect(() => {
    fetchContent();
    fetchComments();
  }, [contentId]);

  const fetchContent = async () => {
    try {
      const token = localStorage.getItem("standari_token");
      const response = await axios.get(`${API}/processed/${contentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setContent(response.data);
      setEditedText(response.data.cleaned_text);
    } catch (error) {
      toast.error("Failed to load content");
      navigate("/dashboard");
    } finally {
      setLoading(false);
    }
  };

  const fetchComments = async () => {
    try {
      const token = localStorage.getItem("standari_token");
      const response = await axios.get(`${API}/comments/${contentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setComments(response.data.comments || []);
    } catch (error) {
      console.error("Failed to load comments");
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem("standari_token");
      await axios.put(
        `${API}/processed/${contentId}`,
        { cleaned_text: editedText },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Changes saved successfully!");
    } catch (error) {
      toast.error("Failed to save changes");
    } finally {
      setSaving(false);
    }
  };

  const handleExport = async () => {
    try {
      const token = localStorage.getItem("standari_token");
      
      // Check if it's a standards-based format (V2 API)
      const v2Formats = ["dita", "s1000d", "mil_40051", "mil_38784", "iso_15288", "ata_ispec2200", "hl7_fhir_r5"];
      
      if (v2Formats.includes(exportFormat)) {
        // Use Export V2 API for standards-based exports with chosen output format
        const response = await axios.post(
          `${API}/export-v2`,
          {
            content_id: contentId,
            format: exportFormat,
            output_format: outputFormat,  // pdf, docx, xml, or zip
            options: {}
          },
          {
            headers: { Authorization: `Bearer ${token}` },
            responseType: 'blob'
          }
        );
        
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        
        // Set appropriate filename based on output format
        const extensions = { pdf: 'pdf', docx: 'docx', xml: 'xml', zip: 'zip' };
        const ext = extensions[outputFormat] || 'zip';
        link.setAttribute('download', `${exportFormat}_export.${ext}`);
        
        document.body.appendChild(link);
        link.click();
        link.remove();
        toast.success(`Exported as ${outputFormat.toUpperCase()}!`);
        return;
      }
      
      // For Word and HTML, use legacy export
      if (exportFormat === "docx" || exportFormat === "html") {
        const response = await axios.post(
          `${API}/export`,
          { processed_content_id: contentId, export_format: exportFormat },
          { 
            headers: { Authorization: `Bearer ${token}` },
            responseType: 'blob'
          }
        );
        
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `document_${contentId}.${exportFormat}`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        toast.success(`Exported as ${exportFormat.toUpperCase()}!`);
        return;
      }
      
      // For legacy XML formats, show preview
      const response = await axios.post(
        `${API}/export`,
        { processed_content_id: contentId, export_format: exportFormat },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setExportData(response.data);
      toast.success(`Exported as ${exportFormat.toUpperCase()}!`);
    } catch (error) {
      console.error("Export error:", error);
      toast.error(error.response?.data?.detail || "Export failed");
    }
  };

  const addComment = async () => {
    if (!newComment.trim()) return;
    
    try {
      const token = localStorage.getItem("standari_token");
      await axios.post(
        `${API}/comments`,
        { content_id: contentId, text: newComment },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNewComment("");
      fetchComments();
      toast.success("Comment added");
    } catch (error) {
      toast.error("Failed to add comment");
    }
  };

  const handleTranslate = async () => {
    setTranslating(true);
    try {
      const token = localStorage.getItem("standari_token");
      const response = await axios.post(
        `${API}/translate`,
        { content_id: contentId, target_language: targetLang },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.translated_content?.translated_text) {
        setEditedText(response.data.translated_content.translated_text);
        toast.success(`Translated to ${targetLang.toUpperCase()}`);
      }
    } catch (error) {
      toast.error("Translation failed");
    } finally {
      setTranslating(false);
    }
  };

  const downloadXML = () => {
    if (!exportData) return;
    const blob = new Blob([exportData.xml_content], { type: "application/xml" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `document_${contentId}.${exportFormat}.xml`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="min-h-screen gradient-bg flex items-center justify-center">
        <Loader2 className="h-12 w-12 text-blue-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen gradient-bg" data-testid="editor-page">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <Button variant="ghost" onClick={() => navigate("/dashboard")} data-testid="back-to-dashboard-btn">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Dashboard
              </Button>
              <div className="flex items-center space-x-2">
                <FileText className="h-6 w-6 text-blue-600" />
                <span className="text-xl font-heading font-bold gradient-text">Smart Editor</span>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <Button onClick={handleSave} disabled={saving} variant="outline" data-testid="save-btn">
                <Save className="h-4 w-4 mr-2" />
                {saving ? "Saving..." : "Save"}
              </Button>
              <Button onClick={handleExport} className="btn-primary" data-testid="export-btn">
                <Download className="h-4 w-4 mr-2" />
                Export
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Editor Panel */}
          <div className="lg:col-span-2">
            <Card className="doc-card" data-testid="editor-panel">
              <CardHeader>
                <CardTitle className="font-heading">Content Editor</CardTitle>
                <CardDescription>Edit your document with standards compliance</CardDescription>
              </CardHeader>
              <CardContent>
                <textarea
                  value={editedText}
                  onChange={(e) => setEditedText(e.target.value)}
                  className="editor-container w-full min-h-[600px] resize-none"
                  placeholder="Start editing your content..."
                  data-testid="editor-textarea"
                />
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Validation Notice */}
            {showValidationNotice && ["dita", "s1000d", "mil_40051", "mil_38784", "iso_15288", "ata_ispec2200", "hl7_fhir_r5"].includes(exportFormat) && (
              <Card className="doc-card border-blue-200 bg-blue-50" data-testid="validation-notice">
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <CardTitle className="text-sm font-heading text-blue-900">Validation Notice</CardTitle>
                    <button
                      onClick={() => setShowValidationNotice(false)}
                      className="text-blue-600 hover:text-blue-800 text-xs"
                    >
                      Dismiss
                    </button>
                  </div>
                </CardHeader>
                <CardContent className="text-xs text-blue-800 space-y-2">
                  <p>
                    Exports include automated structural checks for reference.
                  </p>
                  <p className="font-medium">
                    These checks do not constitute official certification or guarantee compliance.
                  </p>
                  <p>
                    Official validation requires qualified personnel and authorized certification where applicable.
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Compliance Keywords Reference */}
            <Card className="doc-card" data-testid="compliance-legend">
              <CardHeader>
                <CardTitle className="text-lg font-heading">Compliance Keywords</CardTitle>
                <CardDescription>Standard requirement indicators</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="space-y-2 text-sm">
                  <div className="flex items-center justify-between p-2 border-b border-slate-200">
                    <span className="font-semibold text-slate-900">SHALL / MUST</span>
                    <span className="text-xs text-slate-600">Mandatory</span>
                  </div>
                  <div className="flex items-center justify-between p-2 border-b border-slate-200">
                    <span className="font-semibold text-slate-900">SHOULD</span>
                    <span className="text-xs text-slate-600">Recommended</span>
                  </div>
                  <div className="flex items-center justify-between p-2 border-b border-slate-200">
                    <span className="font-semibold text-slate-900">MAY</span>
                    <span className="text-xs text-slate-600">Optional</span>
                  </div>
                  <div className="flex items-center justify-between p-2">
                    <span className="font-semibold text-slate-900">WILL</span>
                    <span className="text-xs text-slate-600">Informational</span>
                  </div>
                </div>
                <div className="pt-2 border-t border-slate-200">
                  <p className="text-xs text-slate-600">
                    Keywords follow {content?.standards_mapping?.recommended_standard || 'detected standard'} compliance rules (RFC 2119)
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Export Options */}
            <Card className="doc-card" data-testid="export-options">
              <CardHeader>
                <CardTitle className="text-lg font-heading">Export Options</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-2 block">
                    Select Standard
                  </label>
                  <Select value={exportFormat} onValueChange={setExportFormat}>
                    <SelectTrigger data-testid="format-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="dita" data-testid="format-dita">DITA 1.3 (Darwin Information Typing)</SelectItem>
                      <SelectItem value="s1000d" data-testid="format-s1000d">S1000D Issue 6.1 (Technical Publications)</SelectItem>
                      <SelectItem value="mil_40051" data-testid="format-mil-40051">MIL-STD-40051 (Technical Manual Standards)</SelectItem>
                      <SelectItem value="mil_38784" data-testid="format-mil-38784">MIL-STD-38784 (Technical Manuals Format)</SelectItem>
                      <SelectItem value="iso_15288" data-testid="format-iso-15288">ISO/IEC/IEEE 15288:2023 (Systems Engineering)</SelectItem>
                      <SelectItem value="ata_ispec2200" data-testid="format-ata">ATA iSpec 2200 Rev 2025.1 (Aviation)</SelectItem>
                      <SelectItem value="hl7_fhir_r5" data-testid="format-fhir">HL7 FHIR R5 (Healthcare Interoperability)</SelectItem>
                      <SelectItem value="docx" data-testid="format-docx">Word Document (.docx)</SelectItem>
                      <SelectItem value="html" data-testid="format-html">HTML Document (.html)</SelectItem>
                      <SelectItem value="xml" data-testid="format-xml">Generic XML (.xml)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Output Format Selection for Standards */}
                {["dita", "s1000d", "mil_40051", "mil_38784", "iso_15288", "ata_ispec2200", "hl7_fhir_r5"].includes(exportFormat) && (
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-2 block">
                      Output Format
                    </label>
                    <Select value={outputFormat} onValueChange={setOutputFormat}>
                      <SelectTrigger data-testid="output-format-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pdf">PDF Document</SelectItem>
                        <SelectItem value="docx">Word Document</SelectItem>
                        <SelectItem value="xml">XML Only</SelectItem>
                        <SelectItem value="zip">Complete Package (ZIP)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
                
                {exportData && exportFormat !== "docx" && exportFormat !== "html" && (
                  <Button onClick={downloadXML} variant="outline" className="w-full" data-testid="download-xml-btn">
                    <Download className="h-4 w-4 mr-2" />
                    Download XML
                  </Button>
                )}
              </CardContent>
            </Card>

            {/* Translation */}
            <Card className="doc-card" data-testid="translation-card">
              <CardHeader>
                <CardTitle className="text-lg font-heading flex items-center">
                  <Globe className="h-5 w-5 mr-2 text-blue-600" />
                  Translation
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-2 block">
                    Target Language
                  </label>
                  <Select value={targetLang} onValueChange={setTargetLang}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="es">Spanish (Español)</SelectItem>
                      <SelectItem value="fr">French (Français)</SelectItem>
                      <SelectItem value="de">German (Deutsch)</SelectItem>
                      <SelectItem value="zh">Chinese (中文)</SelectItem>
                      <SelectItem value="ja">Japanese (日本語)</SelectItem>
                      <SelectItem value="ko">Korean (한국어)</SelectItem>
                      <SelectItem value="it">Italian (Italiano)</SelectItem>
                      <SelectItem value="pt">Portuguese (Português)</SelectItem>
                      <SelectItem value="ru">Russian (Русский)</SelectItem>
                      <SelectItem value="ar">Arabic (العربية)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button 
                  onClick={handleTranslate} 
                  disabled={translating}
                  variant="outline" 
                  className="w-full"
                  data-testid="translate-btn"
                >
                  {translating ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Translating...
                    </>
                  ) : (
                    <>
                      <Globe className="h-4 w-4 mr-2" />
                      Translate
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>

            {/* Team Comments */}
            <Card className="doc-card" data-testid="comments-card">
              <CardHeader>
                <CardTitle className="text-lg font-heading flex items-center">
                  <MessageSquare className="h-5 w-5 mr-2 text-blue-600" />
                  Team Comments
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3 max-h-60 overflow-y-auto">
                  {comments.length === 0 ? (
                    <p className="text-sm text-slate-500 text-center py-4">No comments yet</p>
                  ) : (
                    comments.map((comment) => (
                      <div key={comment.id} className="bg-slate-50 p-3 rounded-lg">
                        <div className="flex items-start justify-between mb-1">
                          <span className="text-sm font-semibold text-slate-900">{comment.username}</span>
                          <span className="text-xs text-slate-500">
                            {new Date(comment.created_at).toLocaleDateString()}
                          </span>
                        </div>
                        <p className="text-sm text-slate-700">{comment.text}</p>
                      </div>
                    ))
                  )}
                </div>
                <div className="flex gap-2">
                  <Textarea
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    placeholder="Add a comment..."
                    className="min-h-20"
                    data-testid="comment-input"
                  />
                </div>
                <Button onClick={addComment} className="w-full" variant="outline" data-testid="add-comment-btn">
                  <Send className="h-4 w-4 mr-2" />
                  Add Comment
                </Button>
              </CardContent>
            </Card>

            {/* Structure Info */}
            {content?.structure && (
              <Card className="doc-card" data-testid="structure-info">
                <CardHeader>
                  <CardTitle className="text-lg font-heading">Document Structure</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 text-sm">
                    <pre className="bg-slate-50 p-3 rounded text-xs overflow-auto max-h-40">
                      {JSON.stringify(content.structure, null, 2)}
                    </pre>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* STE Compliance */}
            {content?.ste_compliance && (
              <Card className="doc-card" data-testid="ste-info">
                <CardHeader>
                  <CardTitle className="text-lg font-heading">STE Compliance</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 text-sm">
                    <pre className="bg-slate-50 p-3 rounded text-xs overflow-auto max-h-40">
                      {JSON.stringify(content.ste_compliance, null, 2)}
                    </pre>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Standards Mapping */}
            {content?.standards_mapping && (
              <Card className="doc-card" data-testid="standards-info">
                <CardHeader>
                  <CardTitle className="text-lg font-heading">Standards Mapping</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 text-sm">
                    <pre className="bg-slate-50 p-3 rounded text-xs overflow-auto max-h-40">
                      {JSON.stringify(content.standards_mapping, null, 2)}
                    </pre>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>

        {/* Export Preview */}
        {exportData && (
          <Card className="doc-card mt-8" data-testid="export-preview">
            <CardHeader>
              <CardTitle className="font-heading">Export Preview - {exportFormat.toUpperCase()}</CardTitle>
              <CardDescription>Generated XML output</CardDescription>
            </CardHeader>
            <CardContent>
              <pre className="bg-slate-900 text-green-400 p-4 rounded-lg overflow-auto max-h-96 text-sm">
                {exportData.xml_content}
              </pre>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}